import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { Flex, Grid } from 'components/box';
import { useIsMobile, useIsTablet } from 'hooks/use-media';
import RenderControl from 'components/_shared/widgets/render-control';
import { useObserveElementWidth } from 'hooks/observers';
import {
  ChevronLeft as IconPrevious,
  ChevronRight as IconNext,
} from 'react-feather';
import Button from 'components/button';

interface CarouselSlide {
  element: React.ReactNode;
  id: string | number;
}

interface CarouselProps {
  slides: CarouselSlide[];
  transitionInterval?: number;
  disableAutoplay?: boolean;
  itemsPerSlide?: number[];
}

const findNextIndex = (
  currentIndex: number,
  direction: 'right' | 'left',
  arrayLength: number
): { nextIndex: number; behavior: ScrollBehavior } => {
  if (direction === 'right') {
    if ((currentIndex + 1) % (arrayLength + 1) === 0) {
      return { nextIndex: 1, behavior: 'instant' as ScrollBehavior };
    }
    return {
      nextIndex: (currentIndex + 1) % (arrayLength + 1),
      behavior: 'smooth' as ScrollBehavior,
    };
  }
  if ((currentIndex - 1) % arrayLength === 0) {
    return { nextIndex: arrayLength, behavior: 'instant' as ScrollBehavior };
  }
  return {
    nextIndex: (currentIndex - 1) % arrayLength,
    behavior: 'smooth' as ScrollBehavior,
  };
};

const NavigationButton = ({
  variant,
  onClick,
  disabled,
}: {
  variant: 'left' | 'right';
  onClick: () => void;
  disabled: boolean;
}) => (
  <Button
    position="absolute"
    variant="solid"
    top="calc(50% - 40px)"
    left={variant === 'left' ? '-10px' : undefined}
    right={variant === 'right' ? '-10px' : undefined}
    height="fit-content"
    width="fit-content"
    p="1px"
    zIndex={10}
    borderRadius="50%"
    shadow
    color="white"
    shouldFocus={false}
    hoverColor="white"
    fontColor="darkestGrey"
    onClick={onClick}
    disabled={disabled}
  >
    {variant === 'right' ? <IconNext size="24" /> : <IconPrevious size="24" />}
  </Button>
);

const AutoplayCarousel = ({
  slides,
  transitionInterval = 10000,
  disableAutoplay,
  itemsPerSlide = [1, 1, 1.5],
}: CarouselProps) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const activeIndex = useRef(1);
  const [slideIndexOnScreen, setSlideIndexOnScreen] = useState(1);

  const slidesOnScreen = useMemo(
    () => [slides[slides.length - 1], ...slides, slides[0]],
    [slides]
  );

  const itemsPerSlideForDevice = isMobile
    ? itemsPerSlide[0]
    : isTablet
    ? itemsPerSlide[1]
    : itemsPerSlide[2];

  const {
    width: carouselWidth,
    ref: carouselContainerRef,
  } = useObserveElementWidth<HTMLDivElement>();

  const offset = useMemo(
    () =>
      itemsPerSlideForDevice > 1
        ? carouselWidth / (itemsPerSlideForDevice * 4)
        : 0,
    [carouselWidth, itemsPerSlideForDevice]
  );

  const slideWidth = useMemo(() => carouselWidth / itemsPerSlideForDevice, [
    carouselWidth,
    itemsPerSlideForDevice,
  ]);

  const goToSlide = useCallback(
    (direction: 'right' | 'left') => {
      const { nextIndex, behavior } = findNextIndex(
        activeIndex.current,
        direction,
        slides.length
      );
      activeIndex.current = nextIndex;
      if (carouselContainerRef.current) {
        carouselContainerRef.current?.scrollTo({
          left: slideWidth * activeIndex.current - offset,
          behavior,
        });
      }
      setSlideIndexOnScreen(activeIndex.current);
    },

    [carouselContainerRef, offset, slideWidth, slides.length]
  );

  useEffect(() => {
    if (carouselContainerRef.current) {
      carouselContainerRef.current?.scrollTo({
        left: slideWidth * activeIndex.current - offset,
        behavior: 'auto',
      });
    }
  }, [
    carouselContainerRef,
    carouselWidth,
    itemsPerSlideForDevice,
    offset,
    slideWidth,
  ]);

  useEffect(() => {
    if (!disableAutoplay) {
      const interactionTimeout = setTimeout(() => {
        goToSlide('right');
      }, transitionInterval);
      return () => clearTimeout(interactionTimeout);
    }
  }, [goToSlide, transitionInterval, slideIndexOnScreen, disableAutoplay]);

  return (
    <Grid gridTemplateColumns="1fr" position="relative">
      <NavigationButton
        variant="left"
        onClick={() => goToSlide('left')}
        disabled={false}
      />
      <Flex ref={carouselContainerRef} width="100%" overflow="hidden">
        <Flex
          alignItems="center"
          ref={carouselRef}
          css={css`
            transition: transform 0.5s ease-in-out;
          `}
        >
          {slidesOnScreen.map((slide, idx) => (
            <Flex
              height="100%"
              key={`${slide.id}-${idx}`}
              width={slideWidth}
              className={
                idx === slideIndexOnScreen ||
                // Used for the edge case where the last slide is the first slide
                (idx === 1 && slideIndexOnScreen === slides.length + 1)
                  ? 'active'
                  : undefined
              }
              css={theme => css`
                opacity: 0.4;
                &.active {
                  opacity: 1;
                }
                transition: opacity 0.3s ease-in-out;
                @media ${theme.mediaQueries.tabletDown} {
                  opacity: 1;
                }
              `}
            >
              {slide.element}
            </Flex>
          ))}
        </Flex>
      </Flex>
      <NavigationButton
        variant="right"
        onClick={() => goToSlide('right')}
        disabled={false}
      />
    </Grid>
  );
};

const Wrapper = (props: CarouselProps) => (
  <RenderControl type="client">
    <AutoplayCarousel {...props} />
  </RenderControl>
);

export default Wrapper;
